/* eslint-disable no-alert */
import cloneDeep from 'lodash/cloneDeep';
import * as HTMLElementCreator from '../../../libs/HTML/HTMLElementCreator';
import type { LevelData } from '../../../game/level/LevelData';
import type { EditorPanel } from './types';
import type { EditorInputConfig } from '../utils/createEditorInput';
import { BUTTON_STYLE, SECTION_LABEL_STYLE } from '../EditorOverlayStyles';
import type { RemixColorsConfig, RemixLevelParameters } from '../../level/types';
import { DEFAULT_REMIX_PARAMETERS } from '../../level/RemixLevel';
import { createButton } from '../../../libs/HTML/HTMLElementCreator';
import { getProperConnectivity } from '../../../game/boardMVC/utils/getBoardConnectivity';
import { BoardModel } from '../../../game/boardMVC/models/board/BoardModel';
import type { EditorCheckBoxConfig } from '../utils/createEditorCheckBox';
import { createEditorCheckBox } from '../utils/createEditorCheckBox';
import { createEditorIncrementInput } from '../utils/createEditorIncrementInput';

interface ConnectivityStatusElements {
  panel: HTMLDivElement;
  content: HTMLDivElement;
}

const checkBoxStyles: Partial<EditorCheckBoxConfig> = {
  panelStyle: {
    margin: '5px 0px 5px 0px',
  },
  labelStyle: {
    color: 'white',
    baselineShift: '6px',
  },
  inputStyle: {
    position: 'absolute',
    left: '100px',
  },
};

const inputFieldStyles: Partial<EditorInputConfig> = {
  labelStyle: {
    display: 'inline-block',
    marginTop: '5px',
    width: '80px',
  },
  inputStyle: {
    display: 'inline-block',
    width: '40px',
  },
};

export class EditorRemixPanel implements EditorPanel {
  public readonly element: HTMLElement;

  private remixParams = cloneDeep({
    ...DEFAULT_REMIX_PARAMETERS,
  }) as RemixLevelParameters & { colors: RemixColorsConfig };

  private readonly connectivityStatusElements: ConnectivityStatusElements;

  private remixColors = false;

  constructor(private readonly runLevelRemix: (remixParameters: RemixLevelParameters) => Promise<void>) {
    this.element = HTMLElementCreator.createDiv({
      elementID: 'editor-remix-panel',
      parentElement: undefined,
      style: {
        position: 'fixed',
        top: '50px',
        right: '5px',
        padding: '2px 2px 2px 2px',
        color: 'black',
        minWidth: '200px',
        minHeight: '500px',
        backgroundColor: 'rgba(0,0,0,0.25)',
        pointerEvents: 'auto',
      },
    });

    HTMLElementCreator.createLabel({
      parentElement: this.element,
      labelText: 'Board Remix',
      style: { ...SECTION_LABEL_STYLE },
    });

    this.connectivityStatusElements = this.createConnectivityStatusPanel();
    this.createBoardSizeInputs();
    this.createSymmetryInputs();
    this.createThresholdInputs();
    this.createColorInputs();
    this.createRemixButton();
  }

  public destroy(): void {
    this.element.remove();
  }

  public focus(): void {
    this.element.style.visibility = 'visible';
  }

  public blur(): void {
    this.element.style.visibility = 'hidden';
  }

  private createConnectivityStatusPanel(): ConnectivityStatusElements {
    const panel = HTMLElementCreator.createDiv({
      parentElement: this.element,
    });

    HTMLElementCreator.createLabel({
      parentElement: panel,
      labelText: 'Connectivity Status',
      style: { ...SECTION_LABEL_STYLE },
    });

    const content = HTMLElementCreator.createDiv({
      parentElement: panel,
      style: {
        margin: '5px 5px 5px 5px',
        color: 'white',
      },
    });

    return {
      panel,
      content,
    };
  }

  private createColorInputs(): void {
    const parentElement = HTMLElementCreator.createDiv({
      parentElement: this.element,
      style: { paddingBottom: '5px' },
    });

    HTMLElementCreator.createLabel({
      parentElement,
      labelText: 'Colors',
      style: { ...SECTION_LABEL_STYLE },
    });

    createEditorCheckBox({
      ...checkBoxStyles,
      parentElement,
      labelText: 'remix colors',
      onValueChanged: (checked: boolean) => {
        this.remixColors = checked;
      },
      initialValue: this.remixColors,
    });

    [
      {
        labelText: 'Min Repeat',
        initialValue: this.remixParams.colors.minColorRepeat.toString(),
        getDeltaConfig: (value: number): Partial<RemixLevelParameters> => ({
          colors: {
            ...this.remixParams.colors,
            minColorRepeat: value,
          },
        }),
      },
      {
        labelText: 'Max Repeat',
        initialValue: this.remixParams.colors.maxColorRepeat.toString(),
        getDeltaConfig: (value: number): Partial<RemixLevelParameters> => ({
          colors: {
            ...this.remixParams.colors,
            maxColorRepeat: value,
          },
        }),
      },
    ].forEach(({ labelText, initialValue, getDeltaConfig }) => {
      createEditorIncrementInput({
        ...inputFieldStyles,
        parentElement,
        labelText,
        initialValue,
        restrictRegEx: /^\d+$/,
        onValueChanged: (inputValue: string) => {
          const value = parseInt(inputValue, 10);
          this.applyDeltaConfig(getDeltaConfig(value));
        },
      });
    });
  }

  private createSymmetryInputs(): void {
    const parentElement = HTMLElementCreator.createDiv({
      parentElement: this.element,
      style: { paddingBottom: '5px' },
    });

    HTMLElementCreator.createLabel({
      parentElement,
      labelText: 'Symmetry',
      style: { ...SECTION_LABEL_STYLE },
    });

    [
      {
        labelText: 'Horizontal',
        initialValue: this.remixParams.symmetry.horizontal,
        getDeltaConfig: (checked: boolean): Partial<RemixLevelParameters> => ({
          symmetry: {
            ...this.remixParams.symmetry,
            horizontal: checked,
          },
        }),
      },
      {
        labelText: 'Vertical',
        initialValue: this.remixParams.symmetry.vertical,
        getDeltaConfig: (checked: boolean): Partial<RemixLevelParameters> => ({
          symmetry: {
            ...this.remixParams.symmetry,
            vertical: checked,
          },
        }),
      },
      {
        labelText: 'Diagonal',
        initialValue: this.remixParams.symmetry.diagonal,
        getDeltaConfig: (checked: boolean): Partial<RemixLevelParameters> => ({
          symmetry: {
            ...this.remixParams.symmetry,
            diagonal: checked,
          },
        }),
      },
    ].forEach(({ labelText, initialValue, getDeltaConfig }) => {
      createEditorCheckBox({
        ...checkBoxStyles,
        parentElement,
        labelText,
        initialValue,
        onValueChanged: (checked: boolean) => {
          this.applyDeltaConfig(getDeltaConfig(checked));
        },
      });
    });
  }

  private createBoardSizeInputs(): void {
    const parentElement = HTMLElementCreator.createDiv({
      parentElement: this.element,
      style: { paddingBottom: '5px' },
    });

    HTMLElementCreator.createLabel({
      parentElement,
      labelText: 'Board Size',
      style: { ...SECTION_LABEL_STYLE },
    });

    [
      {
        labelText: 'Delta Cols',
        initialValue: this.remixParams.size.deltaCols.toString(),
        getDeltaConfig: (value: number): Partial<RemixLevelParameters> => ({
          size: {
            ...this.remixParams.size,
            deltaCols: value,
          },
        }),
      },
      {
        labelText: 'Delta Rows',
        initialValue: this.remixParams.size.deltaRows.toString(),
        getDeltaConfig: (value: number): Partial<RemixLevelParameters> => ({
          size: {
            ...this.remixParams.size,
            deltaRows: value,
          },
        }),
      },
    ].forEach(({ labelText, initialValue, getDeltaConfig }) => {
      const { input } = createEditorIncrementInput({
        ...inputFieldStyles,
        parentElement,
        labelText,
        initialValue,
        restrictRegEx: /^[0-9()-]+$/,
        onValueChanged: (inputValue: string) => {
          let value = parseInt(inputValue, 10);
          if (Number.isNaN(value)) value = 0;
          input.value = value.toString();
          this.applyDeltaConfig(getDeltaConfig(value));
        },
      });
    });
  }

  private createThresholdInputs(): void {
    const {
      remixParams: { thresholds },
    } = this;

    const parentElement = HTMLElementCreator.createDiv({
      parentElement: this.element,
      style: { paddingBottom: '5px' },
    });

    HTMLElementCreator.createLabel({
      parentElement,
      labelText: 'Thresholds',
      style: { ...SECTION_LABEL_STYLE },
    });

    [
      {
        labelText: 'Connectivity',
        initialValue: thresholds.connectivity.toString(),
        getDeltaConfig: (value: number): Partial<RemixLevelParameters> => ({
          thresholds: {
            ...this.remixParams.thresholds,
            connectivity: value,
          },
        }),
      },
      {
        labelText: 'Stacks',
        initialValue: thresholds.stacks.toString(),
        getDeltaConfig: (value: number): Partial<RemixLevelParameters> => ({
          thresholds: {
            ...this.remixParams.thresholds,
            stacks: value,
          },
        }),
      },
      {
        labelText: 'Open Spaces',
        initialValue: thresholds.openSpaces.toString(),
        getDeltaConfig: (value: number): Partial<RemixLevelParameters> => ({
          thresholds: {
            ...this.remixParams.thresholds,
            openSpaces: value,
          },
        }),
      },
      {
        labelText: 'Blockers',
        initialValue: thresholds.blockers.toString(),
        getDeltaConfig: (value: number): Partial<RemixLevelParameters> => ({
          thresholds: {
            ...this.remixParams.thresholds,
            blockers: value,
          },
        }),
      },
    ].forEach(({ labelText, initialValue, getDeltaConfig }) => {
      createEditorIncrementInput({
        ...inputFieldStyles,
        parentElement,
        labelText,
        initialValue,
        restrictRegEx: /^\d+$/,
        onValueChanged: (inputValue: string) => {
          const value = parseInt(inputValue, 10);
          this.applyDeltaConfig(getDeltaConfig(value));
        },
      });
    });
  }

  private createRemixButton(): HTMLButtonElement {
    return createButton({
      parentElement: this.element,
      labelText: 'Start Remix',
      style: {
        ...BUTTON_STYLE,
        margin: '20px 0px 0px 0px',
        width: '100%',
      },
      onClick: async (): Promise<void> => {
        await this.runLevelRemix({
          ...this.remixParams,
          colors: this.remixColors ? this.remixParams.colors : undefined,
        });
      },
    });
  }

  private applyDeltaConfig(deltaConfig: Partial<RemixLevelParameters>): void {
    this.remixParams = {
      ...this.remixParams,
      ...deltaConfig,
    };
  }

  private updateConnectivityStatus(levelData: LevelData): void {
    const connectivity = getProperConnectivity(
      new BoardModel({
        state: levelData.board,
      }),
    );

    this.connectivityStatusElements.content.innerText = `Current: ${connectivity.current}\nMax: ${connectivity.max}`;
  }

  public update(levelData: LevelData): void {
    this.updateConnectivityStatus(levelData);
  }
}
